import type { TFunction } from 'i18next';
import type {
  AdminOrderExportDpi,
  AdminOrderItem,
  AdminOrderItemStatus,
  AdminOrderStatus,
  AdminOrderSummary,
} from '@/services/adminService';
import type { MeasurementUnit } from '@/types/measurement';

export type AdminWorkspaceMode = 'orders' | 'items';
export type AdminWorkspaceCurrencyCode = 'USD' | 'UAH';
export type AdminWorkspaceItemGroupMode = 'paperType' | 'order';
export type AdminWorkspaceExportDpi = AdminOrderExportDpi;
export type AdminWorkspaceLoadStatus = 'idle' | 'loading' | 'error';

export type AdminWorkspacePreviewMeta = {
  widthIn: number | null;
  heightIn: number | null;
  marginIn: number | null;
  galleryWrapIn: number | null;
  originalWidthPx: number | null;
  originalHeightPx: number | null;
};

export type AdminWorkspaceItemGroup = {
  key: string;
  paper: string;
  printer: string;
  items: AdminOrderItem[];
};

export type AdminWorkspaceOrderGroup = {
  key: string;
  orders: AdminOrderSummary[];
};

export type AdminWorkspaceOrderAction =
  | 'print_label'
  | 'select_pack'
  | 'create_ukrposhta'
  | 'print_ukrposhta'
  | 'show_ukrposhta_summary'
  | 'create_novapost'
  | 'print_novapost'
  | 'set_manual_tracking'
  | 'track_shipment'
  | 'process_refund';

export const MANUALLY_DISABLED_ITEM_STATUSES: ReadonlySet<AdminOrderItemStatus> = new Set([
  'NEW',
  'IN_ORDER',
  'FIRST_ORDER',
  'SECOND_ORDER',
  'PAID',
  'REUPLOADED',
  'IN_DELIVERY',
  'DELIVERED',
  'RETURNED',
  'CANCELLED',
]);

export const isReadonlyItemStatusOption = (
  currentStatus: AdminOrderItemStatus,
  optionStatus: AdminOrderItemStatus,
): boolean => (
  currentStatus !== optionStatus && MANUALLY_DISABLED_ITEM_STATUSES.has(optionStatus)
);

export const PAID_UI_ITEM_STATUS = 'PAID_GROUP' as const;
export const PAID_UI_ITEM_MEMBER_STATUSES = ['FIRST_ORDER', 'SECOND_ORDER', 'PAID'] as const satisfies ReadonlyArray<AdminOrderItemStatus>;
export type AdminOrderItemUiStatus = AdminOrderItemStatus | typeof PAID_UI_ITEM_STATUS;

export const isPaidUiItemStatus = (status: AdminOrderItemStatus): boolean => (
  PAID_UI_ITEM_MEMBER_STATUSES.includes(status as (typeof PAID_UI_ITEM_MEMBER_STATUSES)[number])
);

const ITEM_STATUS_LABEL_DEFAULTS: Record<AdminOrderItemStatus, string> = {
  NEW: 'New',
  IN_ORDER: 'In order',
  FIRST_ORDER: '1st Order',
  SECOND_ORDER: '2nd Order',
  PAID: 'Paid',
  REUPLOAD_IMAGE: 'Reupload Image',
  REUPLOADED: 'Reuploaded',
  READY_TO_PRINT: 'Ready to Print',
  PRINTED: 'Printed',
  IN_DELIVERY: 'In delivery',
  DELIVERED: 'Delivered',
  RETURNED: 'Returned',
  CANCELLED: 'Cancelled',
};

const ITEM_STATUS_LABEL_KEYS: Record<AdminOrderItemStatus, string> = {
  NEW: 'adminOrdersWorkspace.status.NEW',
  IN_ORDER: 'adminOrdersWorkspace.status.IN_ORDER',
  FIRST_ORDER: 'adminOrdersWorkspace.status.FIRST_ORDER',
  SECOND_ORDER: 'adminOrdersWorkspace.status.SECOND_ORDER',
  PAID: 'adminOrdersWorkspace.status.PAID',
  REUPLOAD_IMAGE: 'adminOrdersWorkspace.status.REUPLOAD_IMAGE',
  REUPLOADED: 'adminOrdersWorkspace.status.REUPLOADED',
  READY_TO_PRINT: 'adminOrdersWorkspace.status.READY_TO_PRINT',
  PRINTED: 'adminOrdersWorkspace.status.PRINTED',
  IN_DELIVERY: 'adminOrdersWorkspace.status.IN_DELIVERY',
  DELIVERED: 'adminOrdersWorkspace.status.DELIVERED',
  RETURNED: 'adminOrdersWorkspace.status.RETURNED',
  CANCELLED: 'adminOrdersWorkspace.status.CANCELLED',
};

export const getItemStatusLabel = (t: TFunction, status: AdminOrderItemStatus): string => (
  t(ITEM_STATUS_LABEL_KEYS[status], ITEM_STATUS_LABEL_DEFAULTS[status])
);

const ORDER_STATUS_LABEL_DEFAULTS: Record<AdminOrderStatus, string> = {
  NEW: 'New',
  PAID: 'Paid',
  IN_PRODUCTION: 'In production',
  PRINTED: 'Printed',
  IN_DELIVERY: 'In delivery',
  DELIVERED: 'Delivered',
  RETURNED: 'Returned',
  CANCELLED: 'Cancelled',
};

const ORDER_STATUS_LABEL_KEYS: Record<AdminOrderStatus, string> = {
  NEW: 'adminOrdersWorkspace.status.NEW',
  PAID: 'adminOrdersWorkspace.status.PAID',
  IN_PRODUCTION: 'adminOrdersWorkspace.orderStatus.IN_PRODUCTION',
  PRINTED: 'adminOrdersWorkspace.status.PRINTED',
  IN_DELIVERY: 'adminOrdersWorkspace.status.IN_DELIVERY',
  DELIVERED: 'adminOrdersWorkspace.status.DELIVERED',
  RETURNED: 'adminOrdersWorkspace.status.RETURNED',
  CANCELLED: 'adminOrdersWorkspace.status.CANCELLED',
};

export const getOrderStatusLabel = (
  t: TFunction,
  status: AdminOrderStatus,
): string => (
  t(ORDER_STATUS_LABEL_KEYS[status], ORDER_STATUS_LABEL_DEFAULTS[status])
);

export type AdminWorkspaceItemState = {
  status: AdminWorkspaceLoadStatus;
  error: string | null;
  items: AdminOrderItem[];
  filteredItems: AdminOrderItem[];
  groupedItems: AdminWorkspaceItemGroup[];
  groupMode: AdminWorkspaceItemGroupMode;
  searchInput: string;
  dateFrom: string;
  dateTo: string;
  onlyFirstOrder: boolean;
  statusFilters: Set<AdminOrderItemStatus>;
  updatingIds: Set<string>;
  exportingGroupKeys: Set<string>;
  exportDpiByGroupKey: Record<string, AdminWorkspaceExportDpi>;
  isExportingAll: boolean;
  downloadingWithMarginIds: Set<string>;
  reuploadDialogItem: AdminOrderItem | null;
  reuploadComment: string;
  reuploadSubmitting: boolean;
};

export type AdminWorkspaceOrderState = {
  status: AdminWorkspaceLoadStatus;
  error: string | null;
  orders: AdminOrderSummary[];
  groupedOrders: AdminWorkspaceOrderGroup[];
  statusOptions: string[];
  visibleStatuses: Set<string>;
  orderStatusSavingId: string | null;
  searchInput: string;
  dateFrom: string;
  dateTo: string;
};

export type AdminWorkspaceCommonProps = {
  measurementUnit: MeasurementUnit;
};
